import { useEffect, useRef, useState } from 'react';
import { uc } from '../unifiedContactsApi';
import { phoneDigits } from './phone';
import { primaryEmail } from './ContactSearchInput';

const DEBOUNCE_MS = 300;
const LIMIT = 5;

const last10 = (raw) => String(raw ?? '').replace(/\D/g, '').slice(-10);

// The contact's main phone: the is_primary entry, else the first.
export const primaryPhone = (c) => {
  const list = c?.phones || [];
  return (list.find((p) => p.is_primary) || list[0])?.value || '';
};

// Ticket 174: the fields a picked contact fills into a borrower-text dialog.
export const contactFields = (c) => ({
  name: c?.display_name || '',
  email: primaryEmail(c),
  phone: phoneDigits(primaryPhone(c)),
});

const holds = (c, digits) => (c?.phones || []).some((p) => last10(p.value) === digits);

// Mirrors borrower_sms._is_main_number: GHL texts a contact's main phone, so a
// contact has the number as its main only if it holds the number and has exactly
// one phone, or its is_primary phone is that number. (The backend query already
// guarantees "holds"; here it is checked too, as a pick may carry another number.)
export const isMainNumber = (c, digits) => {
  if (!holds(c, digits)) return false;
  const phones = c.phones || [];
  return phones.length === 1 || phones.some((p) => p.is_primary && last10(p.value) === digits);
};

// Mirrors borrower_sms._find_mirror_contact: of the contacts holding the number,
// the one whose email matches the typed email, else the first.
const choose = (hits, email) => {
  const typed = (email || '').trim().toLowerCase();
  const byEmail = typed && hits.find((c) => (c.emails || []).some((e) => (e.value || '').trim().toLowerCase() === typed));
  return byEmail || hits[0] || null;
};

// Says which contact the backend will text, derived from the phone field alone
// (the send matches on phone, not on whatever was picked). Returns the recipient
// line text (null while the phone is not a full 10-digit number / the lookup is
// still running) and `blocked`: true when the number is only a secondary number
// of an existing contact, which the backend refuses (secondary_number) — the
// dialogs disable Send then. One shared hook for both borrower-text dialogs.
//   phone/email/name: the dialog's field values; picked: the contact last picked.
export default function useTextRecipient({ phone, email, name, picked }) {
  const digits = phoneDigits(phone);
  const full = digits.length === 10;
  const matchesPick = full && !!picked && isMainNumber(picked, digits);
  const [found, setFound] = useState({ digits: '', hits: [] });
  const reqRef = useRef(0);

  useEffect(() => {
    const req = ++reqRef.current; // any earlier lookup is now stale
    if (!full || matchesPick) return undefined;
    const timer = setTimeout(async () => {
      let hits = [];
      try {
        const r = await uc.search(digits, LIMIT);
        hits = ((r && r.contacts) || []).filter((c) => holds(c, digits));
      } catch {
        return; // no line rather than a wrong one
      }
      if (reqRef.current === req) setFound({ digits, hits });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [digits, full, matchesPick]);

  const none = { line: null, blocked: false };
  if (!full) return none;
  if (matchesPick) return { line: `Will text ${picked.display_name}`, blocked: false };
  if (found.digits !== digits) return none;
  if (found.hits.length === 0) {
    return { line: `New contact: ${(name || '').trim() || 'Borrower'}`, blocked: false };
  }
  const main = choose(found.hits.filter((c) => isMainNumber(c, digits)), email);
  if (main) return { line: `Will text ${main.display_name}`, blocked: false };
  return {
    line: `${found.hits[0].display_name} has this as a second number — use their main number`,
    blocked: true,
  };
}
